/**
 * <DemoProvider> — interactive guided tour.
 *
 * State machine:
 *   idle  →  prompt(0)  ⇄  affirm(0)  →  prompt(1)  →  affirm(1)  →  …  →  finished
 *
 *   - In `prompt`: the narrator shows an instruction next to the target
 *     element. The provider installs an event listener matching the beat's
 *     `await` spec; when the user performs the action, we flip to `affirm`.
 *   - In `affirm`: we show a short praise message and auto-advance to the
 *     next beat's prompt after `affirmHoldMs` (default 1500 ms).
 *   - Some beats (cold opens, finales) have `await: { kind: 'auto', ms }`
 *     so they self-advance — useful when there's nothing for the user
 *     to interact with.
 *
 * Activation paths:
 *   - Manual button (header).
 *   - `autoStart` prop  → marketing landing page.
 *   - `?demo=1` URL flag → shareable deep link.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { SCRIPT, DEMO_PRELOAD } from "./script";
import { fetchKanjiDetail, fetchWord } from "@/lib/api";

const DemoCtx = createContext(null);
export const useDemo = () => useContext(DemoCtx);

export function DemoProvider({ children, autoStart = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  // status: 'idle' | 'prompt' | 'affirm' | 'finished'
  const [status, setStatus] = useState("idle");
  const [step, setStep] = useState(0);
  const advanceTimerRef = useRef(null);

  const clearAdvance = () => {
    if (advanceTimerRef.current) {
      clearTimeout(advanceTimerRef.current);
      advanceTimerRef.current = null;
    }
  };

  const preload = useCallback(() => {
    for (const ch of DEMO_PRELOAD.kanji) {
      qc.prefetchQuery({
        queryKey: ["kanji", ch],
        queryFn: () => fetchKanjiDetail(ch),
      });
    }
    for (const w of DEMO_PRELOAD.words) {
      qc.prefetchQuery({
        queryKey: ["word", w],
        queryFn: () => fetchWord(w),
      });
    }
  }, [qc]);

  const beat = SCRIPT[step] || null;

  // Run a beat's optional `setup` action whenever we enter `prompt` mode.
  // Setup is the demo's chance to put the app in the right state (route,
  // tab) so the user has the right thing in front of them to act on.
  useEffect(() => {
    if (status !== "prompt" || !beat) return;
    let cancelled = false;
    (async () => {
      if (beat.setup) {
        try {
          await beat.setup({ navigate });
        } catch (e) {
          console.warn("[demo] setup failed", e);
        }
      }
      if (cancelled) return;
    })();
    return () => {
      cancelled = true;
    };
  }, [step, status]);

  // Install the user-action detector for the current beat's `await`. When
  // the action is detected, flip to `affirm`.
  useEffect(() => {
    if (status !== "prompt" || !beat?.await) return undefined;
    const a = beat.await;
    const complete = () => setStatus("affirm");

    if (a.kind === "auto") {
      const t = setTimeout(complete, a.ms || 3500);
      return () => clearTimeout(t);
    }
    if (a.kind === "ack") {
      // Pure-acknowledgement beats: nothing in the DOM to interact with,
      // a teaching message to absorb. The narrator shows a "Got it" button
      // that fires the custom `demo:ack` event when clicked. The user
      // controls the pace — nothing auto-advances.
      const handler = () => complete();
      document.addEventListener("demo:ack", handler);
      return () => document.removeEventListener("demo:ack", handler);
    }
    if (a.kind === "input-contains") {
      const handler = (e) => {
        const el = e.target;
        if (
          el?.getAttribute?.("data-testid") === a.testId &&
          (el.value || "").toLowerCase().includes(a.value.toLowerCase())
        ) {
          // Tiny delay so the LAST keystroke (e.g. the 'e' in "time" or
          // "blue") gets a paint cycle before the demo flips away.
          // Without this, the user types the final letter, the demo
          // advances on the same React tick, and the typed character
          // never visibly lands on screen.
          setTimeout(complete, 450);
        }
      };
      document.addEventListener("input", handler, true);
      return () => document.removeEventListener("input", handler, true);
    }
    if (a.kind === "click") {
      const handler = (e) => {
        let el = e.target;
        while (el && el !== document) {
          const id = el.getAttribute?.("data-testid");
          if (id && (id === a.testId || (a.prefix && id.startsWith(a.prefix)))) {
            complete();
            return;
          }
          el = el.parentElement;
        }
      };
      document.addEventListener("click", handler, true);
      return () => document.removeEventListener("click", handler, true);
    }
    if (a.kind === "route-includes") {
      const check = () => {
        if (window.location.pathname.includes(a.substr)) complete();
      };
      check();
      const interval = setInterval(check, 200);
      return () => clearInterval(interval);
    }
    if (a.kind === "hover-part") {
      const handler = (e) => {
        let el = e.target;
        while (el && el !== document) {
          if (el.getAttribute?.("data-demo-part") === a.char) {
            complete();
            return;
          }
          el = el.parentElement;
        }
      };
      document.addEventListener("mouseenter", handler, true);
      // Also accept a tap on mobile (no hover).
      document.addEventListener("click", handler, true);
      return () => {
        document.removeEventListener("mouseenter", handler, true);
        document.removeEventListener("click", handler, true);
      };
    }
    return undefined;
  }, [status, step, beat]);

  // When entering `affirm`, wait for the user to acknowledge the
  // teaching message before advancing to the next beat. The narrator
  // renders a "Got it · Next" button that dispatches `demo:next`. The
  // user controls all pacing — nothing in the demo auto-advances now.
  useEffect(() => {
    if (status !== "affirm" || !beat) return undefined;
    const handler = () => {
      const next = step + 1;
      if (next >= SCRIPT.length) {
        setStatus("finished");
      } else {
        setStep(next);
        setStatus("prompt");
      }
    };
    document.addEventListener("demo:next", handler);
    return () => document.removeEventListener("demo:next", handler);
  }, [status, step, beat]);

  // Auto-start: prop OR ?demo=1 URL flag. Fires once per mount.
  const autoStartedRef = useRef(false);
  useEffect(() => {
    if (autoStartedRef.current) return;
    const search = new URLSearchParams(location.search);
    const urlFlag = search.get("demo") === "1";
    if (!autoStart && !urlFlag) return;
    autoStartedRef.current = true;
    if (urlFlag) {
      search.delete("demo");
      const next = `${location.pathname}${search.toString() ? "?" + search.toString() : ""}`;
      navigate(next, { replace: true });
    }
    setTimeout(() => {
      preload();
      setStep(0);
      setStatus("prompt");
    }, 250);
  }, [autoStart]);

  const controls = useMemo(
    () => ({
      start: () => {
        preload();
        clearAdvance();
        setStep(0);
        setStatus("prompt");
      },
      prev: () => {
        clearAdvance();
        const ns = Math.max(0, step - 1);
        setStep(ns);
        setStatus("prompt");
      },
      restart: () => {
        clearAdvance();
        setStep(0);
        setStatus("prompt");
      },
      stop: () => {
        clearAdvance();
        setStatus("idle");
        setStep(0);
        navigate("/");
      },
    }),
    [step, status, preload, navigate],
  );

  const value = useMemo(
    () => ({ status, step, beat, total: SCRIPT.length, controls }),
    [status, step, beat, controls],
  );

  return <DemoCtx.Provider value={value}>{children}</DemoCtx.Provider>;
}
